import React, { useCallback, useState } from 'react';
import MapGL, { Marker, Source, Layer } from 'react-map-gl';
import type { LayerProps } from 'react-map-gl';
import { Flex, Input, Layout, Space } from 'antd';
import 'mapbox-gl/dist/mapbox-gl.css';
import { LOCAL_EN, THEME_VARIABLE } from '@/config';
import { LocationOutlineIcon, MarkerIcon, ScanCodeOutlineIcon, SearchIcon } from '../Icons';
import useMapCoordinates, { MAPBOX_TOKEN } from '@/hooks/useMapCoordinates';
import { Heading } from '../Heading';
import { useTranslation } from 'react-i18next';
import { LocateFixed, ShieldAlert, CheckCircle2 } from 'lucide-react';
import {
  BAGHDAD_BOUNDS,
  BAGHDAD_CENTER,
  BAGHDAD_MASK_GEOJSON,
  BAGHDAD_BORDER_GEOJSON,
  isInsideBaghdad,
} from '@/utils/helpers/mapGeofence';

export interface CordsProps {
  latitude: number;
  longitude: number;
}

interface AddCoordinatesProps {
  onCoordinatesChange: (coords: CordsProps) => void;
  title: string;
  searchPlaceholder: string;
  coordinates?: CordsProps | null;
}

/* ── Map overlay layers ── */
const maskLayer: LayerProps = {
  id: 'baghdad-mask-modal',
  type: 'fill',
  paint: {
    'fill-color': '#000000',
    'fill-opacity': 0.35,
  },
};

const borderLayer: LayerProps = {
  id: 'baghdad-border-modal',
  type: 'line',
  paint: {
    'line-color': '#3b82f6',
    'line-width': 2.5,
    'line-opacity': 0.7,
    'line-dasharray': [3, 2],
  },
};

const borderGlowLayer: LayerProps = {
  id: 'baghdad-border-glow-modal',
  type: 'line',
  paint: {
    'line-color': '#3b82f6',
    'line-width': 8,
    'line-opacity': 0.15,
    'line-blur': 6,
  },
};

const AddCoordinates: React.FC<AddCoordinatesProps> = ({
  onCoordinatesChange,
  title,
  searchPlaceholder,
  coordinates,
}) => {
  const { i18n, t } = useTranslation();
  const [outsideWarning, setOutsideWarning] = useState(false);
  const isValid = coordinates ? isInsideBaghdad(coordinates.latitude, coordinates.longitude) : true;

  const handleValidatedCoordinatesChange = useCallback(
    (coords: CordsProps) => {
      if (isInsideBaghdad(coords.latitude, coords.longitude)) {
        setOutsideWarning(false);
        onCoordinatesChange(coords);
      } else {
        setOutsideWarning(true);
        setTimeout(() => setOutsideWarning(false), 3000);
      }
    },
    [onCoordinatesChange],
  );

  const { viewport, handleViewportChange, handleInputChange, handleMapClick } =
    useMapCoordinates(handleValidatedCoordinatesChange);

  const handleRecenter = () => {
    // Trigger a viewport change by dispatching a move
    handleViewportChange({
      viewState: {
        ...viewport,
        latitude: BAGHDAD_CENTER.lat,
        longitude: BAGHDAD_CENTER.lng,
        zoom: 11,
      },
    } as any);
  };

  return (
    <Flex vertical className='mb-8'>
      <Flex className='relative items-center justify-between'>
        <Flex className='items-center'>
          <Heading
            title={title}
            fontSize='text-lg'
            fontWeight='font-bold'
            extraClassName='uppercase'
          />
          <span className='relative text-customRed text-base font-bold mx-[2px]'>*</span>
        </Flex>
        <span className='text-[10px] text-blue-600 font-medium px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100'>
          {t('baghdad_service_area')}
        </span>
      </Flex>
      <Layout className='h-[240px] w-full rounded-[20px] overflow-hidden mt-[14px] relative'>
        <MapGL
          {...viewport}
          onMove={handleViewportChange}
          mapStyle='mapbox://styles/mapbox/streets-v11'
          mapboxAccessToken={MAPBOX_TOKEN}
          maxBounds={BAGHDAD_BOUNDS}
          onClick={handleMapClick}
        >
          {/* Dark mask outside Baghdad */}
          <Source id='baghdad-mask-modal' type='geojson' data={BAGHDAD_MASK_GEOJSON}>
            <Layer {...maskLayer} />
          </Source>

          {/* Border glow + dashed line */}
          <Source id='baghdad-border-modal' type='geojson' data={BAGHDAD_BORDER_GEOJSON}>
            <Layer {...borderGlowLayer} />
            <Layer {...borderLayer} />
          </Source>

          <Space
            className={`absolute z-10 ${
              i18n.language === LOCAL_EN ? 'top-3 left-3' : 'top-3 right-3'
            }`}
          >
            <Input
              prefix={<SearchIcon color={THEME_VARIABLE.customGrayShade5} />}
              placeholder={searchPlaceholder}
              className='rounded-[10px] border border-solid border-customGrayShade4 w-[297px] py-2 placeholder:text-sm placeholder:font-normal placeholder:leading-[18px] placeholder:text-customGrayShade5'
              onChange={handleInputChange}
            />
          </Space>

          {/* Recenter button */}
          <button
            type='button'
            onClick={handleRecenter}
            className={`absolute z-10 top-3 ${i18n.language === LOCAL_EN ? 'right-3' : 'left-3'} p-2 rounded-lg bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm hover:bg-white/95 transition-all duration-200`}
            title={String(t('recenter_map'))}
          >
            <LocateFixed className='h-4 w-4 text-gray-500 hover:text-gray-700' />
          </button>

          <Space className='absolute bottom-3 left-3 z-10 bg-customYellowPrimary rounded-full w-6 h-6 flex items-center justify-center'>
            <ScanCodeOutlineIcon
              className={`${i18n.language === LOCAL_EN ? 'mt-1 ml-[1px]' : 'mt-[6px]'}`}
            />
          </Space>
          <Space className='absolute bottom-3 right-3 z-10 bg-customYellowPrimary rounded-full w-6 h-6 flex items-center justify-center'>
            <LocationOutlineIcon className='mt-1 ml-[1px]' />
          </Space>
          {coordinates && (
            <Marker latitude={coordinates?.latitude} longitude={coordinates?.longitude}>
              <MarkerIcon />
            </Marker>
          )}
        </MapGL>

        {/* Outside service area warning */}
        <div
          className={`absolute bottom-10 left-3 right-3 z-20 transition-all duration-300 ${
            outsideWarning
              ? 'translate-y-0 opacity-100'
              : 'translate-y-2 opacity-0 pointer-events-none'
          }`}
        >
          <div className='flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/90 backdrop-blur-sm text-white text-xs font-medium shadow-lg'>
            <ShieldAlert className='h-4 w-4 shrink-0' />
            <span>{t('location_outside_service_area')}</span>
          </div>
        </div>

        {/* Valid location indicator */}
        {coordinates && isValid && !outsideWarning && (
          <div className='absolute bottom-10 left-3 z-20'>
            <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm text-xs text-emerald-600 font-medium'>
              <CheckCircle2 className='h-3.5 w-3.5' />
              <span>{t('inside_service_area')}</span>
            </div>
          </div>
        )}
      </Layout>
    </Flex>
  );
};

export default AddCoordinates;
